import { getValue, setValue } from 'utils/indexedDB';

export const CHANGE_EVENT = 'heltar:test-widget-changed';

/** Re-run `onChange` whenever the test-widget state changes (this tab or another). */
export const subscribeTestWidget = (onChange: () => void): (() => void) => {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

const ACTIVE_USER_KEY = 'heltar:test-widget-user';
const BOT_SESSIONS_KEY = 'heltar:test-widget-bot-sessions';
const STORE = 'web_widget_test_users';

export const DEFAULT_HEADER_TITLE = 'Widget test';
export const DEFAULT_HEADER_SUBTITLE =
  'Messages land in your inbox under "Test (…)"';

export interface TestWidgetUser {
  id: string;
  name: string;
  createdAt: number;
}

export type ActiveTestUser = Pick<TestWidgetUser, 'id' | 'name'>;

const NONCE_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const NONCE_LEN = 12;

export const freshTestNonce = (): string => {
  let out = '';
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c?.getRandomValues) {
    const buf = new Uint32Array(NONCE_LEN);
    c.getRandomValues(buf);
    for (let i = 0; i < NONCE_LEN; i++)
      out += NONCE_ALPHABET[buf[i] % NONCE_ALPHABET.length];
  } else {
    for (let i = 0; i < NONCE_LEN; i++)
      out += NONCE_ALPHABET[Math.floor(Math.random() * NONCE_ALPHABET.length)];
  }
  return out;
};

export const makeTestVisitorId = (
  employeeId: number | string,
  nonce: string = freshTestNonce(),
): string => `test-${employeeId}-${nonce}`;

export const belongsToEmployee = (
  id: string | undefined | null,
  employeeId: number | string,
): boolean => {
  if (!id) return false;
  const own = `test-${employeeId}`;
  return id === own || id.startsWith(`${own}-`);
};

export const resolveTesterIdentity = (
  name: string | undefined,
  employeeName: string | undefined,
): { visitorName: string; headerTitle: string } => {
  const trimmed = (name ?? '').trim();
  if (trimmed) return { visitorName: trimmed, headerTitle: trimmed };
  return {
    visitorName: `Test (${employeeName ?? 'Admin'})`,
    headerTitle: DEFAULT_HEADER_TITLE,
  };
};

const usersKey = (businessId: number | string, employeeId: number | string) =>
  `${businessId}:${employeeId}`;

export const loadTestUsers = async (
  businessId: number | string,
  employeeId: number | string,
): Promise<TestWidgetUser[]> =>
  (await getValue<TestWidgetUser[]>(STORE, usersKey(businessId, employeeId))) ??
  [];

export const saveTestUsers = (
  businessId: number | string,
  employeeId: number | string,
  users: TestWidgetUser[],
): Promise<void> => setValue(STORE, usersKey(businessId, employeeId), users);

export const getActiveTestUser = (): ActiveTestUser | null => {
  try {
    const raw = localStorage.getItem(ACTIVE_USER_KEY);
    return raw ? (JSON.parse(raw) as ActiveTestUser) : null;
  } catch {
    return null;
  }
};

export const setActiveTestUser = (user: ActiveTestUser | null): void => {
  try {
    if (user) localStorage.setItem(ACTIVE_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(ACTIVE_USER_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const ensureActiveTestUser = async (
  businessId: number,
  employeeId: number,
): Promise<ActiveTestUser> => {
  const current = getActiveTestUser();
  if (current && belongsToEmployee(current.id, employeeId)) return current;

  const users = await loadTestUsers(businessId, employeeId);
  const reusable = users.find(u => belongsToEmployee(u.id, employeeId));
  if (reusable) {
    const active = { id: reusable.id, name: reusable.name };
    setActiveTestUser(active);
    return active;
  }

  const fresh: TestWidgetUser = {
    id: makeTestVisitorId(employeeId),
    name: '',
    createdAt: Date.now(),
  };
  await saveTestUsers(businessId, employeeId, [...users, fresh]);
  const active = { id: fresh.id, name: fresh.name };
  setActiveTestUser(active);
  return active;
};

/** chatbotId → visitor id. A bot's preview keeps one conversation until
 *  "New chat" swaps in a fresh visitor; another employee's ids are ignored. */
const readBotSessions = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(BOT_SESSIONS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

export const startBotTestSession = (
  chatbotId: string,
  employeeId: number | string,
): string => {
  const id = makeTestVisitorId(employeeId);
  try {
    localStorage.setItem(
      BOT_SESSIONS_KEY,
      JSON.stringify({ ...readBotSessions(), [chatbotId]: id }),
    );
  } catch {
    /* ignore */
  }
  return id;
};

export const ensureBotTestVisitorId = (
  chatbotId: string,
  employeeId: number | string,
): string => {
  const current = readBotSessions()[chatbotId];
  return belongsToEmployee(current, employeeId)
    ? current
    : startBotTestSession(chatbotId, employeeId);
};

/** Inbox name of a bot's preview session — tester plus bot, so per-bot threads are told apart. */
export const botSessionVisitorName = (
  employeeName: string | undefined,
  botName: string,
): string =>
  `${resolveTesterIdentity(undefined, employeeName).visitorName} · ${botName}`;

export interface TestWidgetMountParams {
  businessId: number;
  apiHost: string;
  /** Dashboard theme — the widget follows it unless the saved config sets a mode. */
  theme: 'light' | 'dark' | 'system';
  /** Saved Settings → Web Chat Widget customisation (mode, prompt, theme). */
  config: Record<string, unknown>;
  activeUser: ActiveTestUser;
  employeeName?: string;
  hash?: string | null;
  /** Bot under test (pinned for this visitor); absent = plain widget test. */
  chatbotId?: string;
  botName?: string;
  /** Open the panel on mount (the playground preview); omit for the launcher. */
  isOpen?: boolean;
}

export const buildTestWidgetProps = (p: TestWidgetMountParams) => {
  const { config } = p;
  const cfgTheme =
    config.theme && typeof config.theme === 'object'
      ? (config.theme as Record<string, unknown>)
      : {};
  const { visitorName, headerTitle } = resolveTesterIdentity(
    p.activeUser.name,
    p.employeeName,
  );
  return {
    businessId: p.businessId,
    apiHost: p.apiHost,
    mode: typeof config.mode === 'string' ? config.mode : p.theme,
    ...(typeof config.dynamicPrompt === 'string'
      ? { dynamicPrompt: config.dynamicPrompt }
      : {}),
    // A bot test force-enables calls so the call path can be exercised (it
    // rings the published voice bot or the dashboard, never the pinned bot).
    enableCall: config.enableCall === true || !!p.chatbotId,
    ...(p.isOpen === undefined ? {} : { isOpen: p.isOpen }),
    visitor: {
      id: p.activeUser.id,
      name: visitorName,
      ...(p.hash ? { hash: p.hash } : {}),
    },
    theme: {
      headerTitle: p.botName ?? headerTitle,
      headerSubtitle: p.botName ? 'Bot test session' : DEFAULT_HEADER_SUBTITLE,
      ...cfgTheme,
    },
  };
};
